import {
  logCurrentConfig,
  logDebug,
  logWarn,
  LogLevel,
  setLogLevel,
} from '@ptek/client-logger';
import { BaseWidget } from '@ptek/widget-core';
import App from './components/App';
import { updateApiRouterForMigration } from './config/api';
import { setHostSeamlessAuthConfig } from './config/auth';
import {
  AmbientScribeContext,
  AmbientScribeWidgetConfig,
  HostSeamlessAuthConfig,
} from './types';
import { isStandaloneMode } from './utils/standaloneMode';

export class AmbientScribeWidget extends BaseWidget<AmbientScribeContext> {
  /**
   * The single live scribe widget in this browser tab/JS context. Only one
   * AmbientScribeWidget may be mounted at a time: a second concurrent instance
   * would share the module-level WhisperClient singleton and clobber the first's
   * sessionId/callbacks/WebSocket (cross-patient transcript loss). In-memory,
   * per-tab only (no cross-tab locking by design).
   */
  private static activeInstance: AmbientScribeWidget | null = null;

  private static getViteLogLevel(): LogLevel | null {
    try {
      const envValue = import.meta.env.VITE_LOG_LEVEL;
      if (!envValue?.trim()) return null;

      const level = envValue.toLowerCase().trim() as LogLevel;
      const validLevels: LogLevel[] = ['off', 'error', 'warn', 'debug'];

      return validLevels.includes(level) ? level : null;
    } catch {
      return null;
    }
  }

  private static resolveLogLevel(configLogLevel: LogLevel): LogLevel {
    const envLogLevel = AmbientScribeWidget.getViteLogLevel();
    return envLogLevel ?? configLogLevel;
  }

  /**
   * Host seamless auth config captured at construction and applied to the
   * shared auth module in `mount()`, never here: `setHostSeamlessAuthConfig`
   * overwrites a module singleton and purges the cached host token, so a second
   * instance whose mount is refused must not run it — that would swap a live
   * instance's token callback (possibly another tenant's) mid-session.
   */
  private readonly hostSeamlessAuth: HostSeamlessAuthConfig | undefined;

  constructor(
    config: AmbientScribeWidgetConfig = { id: 'ambient-scribe-widget' }
  ) {
    const {
      initialContext = {},
      ptekHeaders,
      hostSeamlessAuth,
      standalone = false,
      logLevel = 'error',
      brand,
      ...rest
    } = config;

    setLogLevel(AmbientScribeWidget.resolveLogLevel(logLevel));
    logCurrentConfig();

    if (ptekHeaders) {
      logDebug('Widget', 'PTek headers are deprecated and not used');
    }

    const mergedContext: AmbientScribeContext = {
      features: {
        ...(initialContext.features || {}),
      },
      standalone,
      ...initialContext,
    };

    updateApiRouterForMigration();

    logDebug('Widget', 'API router updated for migration');

    const AppComponent = App;

    const useStandaloneDimensions = isStandaloneMode({ standalone });

    // --- sizeConstraints helpers ---

    /**
     * Returns true if `v` is a positive finite number or a widget-core unit
     * string (a decimal number followed by px, %, vw, vh, rem, or em). Plain
     * bare numbers as strings (e.g. "425") are also accepted (widget-core
     * treats the missing unit as px).
     */
    const isValidSizeValue = (v: number | string): boolean => {
      if (typeof v === 'number') {
        return Number.isFinite(v) && v > 0;
      }
      const trimmed = String(v).trim();
      return (
        /^\d+(?:\.\d+)?(?:px|%|vw|vh|rem|em)?$/.test(trimmed) &&
        parseFloat(trimmed) > 0
      );
    };

    /**
     * Resolves a size value to px for min>max comparison. Uses the current
     * viewport at construction time (same assumption as widget-core SizeResolver).
     * rem/em assume 16 px (SizeResolver default).
     */
    const resolvePx = (v: number | string, refPx: number): number => {
      if (typeof v === 'number') return v;
      const trimmed = String(v).trim();
      const m = trimmed.match(/^(\d+(?:\.\d+)?)(px|%|vw|vh|rem|em)?$/);
      if (!m) return parseFloat(trimmed) || 0;
      const n = parseFloat(m[1]);
      const unit = m[2] || 'px';
      switch (unit) {
        case 'px':
          return n;
        case '%':
          return (refPx * n) / 100;
        case 'vw':
          return ((window.innerWidth || 0) * n) / 100;
        case 'vh':
          return ((window.innerHeight || 0) * n) / 100;
        case 'rem':
        case 'em':
          return 16 * n;
        default:
          return n;
      }
    };

    /**
     * Returns the host-supplied value when it is valid, otherwise logs a
     * warning and returns `fallback`. Only used in embedded mode; standalone
     * keeps its own fixed dimensions.
     */
    const resolveEmbeddedSize = <F extends number | string | undefined>(
      hostValue: number | string | undefined,
      fallback: F,
      label: string
    ): number | string | F => {
      if (hostValue === undefined || hostValue === null) return fallback;
      if (!isValidSizeValue(hostValue)) {
        logWarn(
          'Widget',
          `sizeConstraints.${label} "${hostValue}" is not a valid size value; falling back to ${fallback}. ` +
            'Expected a positive number or a unit string (px, %, vw, vh, rem, em).'
        );
        return fallback;
      }
      return hostValue;
    };

    const widgetMaxHeightSize = (): number | string => {
      const currentViewportHeight = window.innerHeight;
      if (currentViewportHeight < 800) {
        return '85%';
      }
      return 800;
    };

    // Collapsed dimensions (minWidth / minHeight). String values are now
    // honored for embedded mode; standalone only ever uses plain numbers.
    // Declared with `let` so the min > max guard below can reset them.
    let collapsedWidth: number | string = useStandaloneDimensions
      ? 425
      : resolveEmbeddedSize(rest.sizeConstraints?.minWidth, 425, 'minWidth');

    let collapsedHeight: number | string = useStandaloneDimensions
      ? 200
      : resolveEmbeddedSize(rest.sizeConstraints?.minHeight, 200, 'minHeight');

    // Expanded dimensions (maxWidth / maxHeight) — embedded mode only.
    // Default (not resizable): maxWidth falls back to the collapsed width;
    // maxHeight falls back to the 800/85% rule. When the host opts in to
    // drag-resize (`capabilities.resizable: true`) and passes no max, the max
    // is left unset so the user can drag up to what the viewport allows.
    // Host-supplied max values apply in both cases and use the same validator.
    const hostResizable = rest.capabilities?.resizable === true;

    let expandedWidth: number | string | undefined = useStandaloneDimensions
      ? undefined
      : resolveEmbeddedSize(
          rest.sizeConstraints?.maxWidth,
          hostResizable ? undefined : collapsedWidth,
          'maxWidth'
        );

    let expandedHeight: number | string | undefined = useStandaloneDimensions
      ? undefined
      : resolveEmbeddedSize(
          rest.sizeConstraints?.maxHeight,
          hostResizable ? undefined : widgetMaxHeightSize(),
          'maxHeight'
        );

    // Warn when resolved min > max (would produce an unusable layout).
    // Reset both dimensions to their defaults so the widget always has a
    // consistent state — reassigning `let` variables here is safe because
    // super() hasn't been called yet.
    if (!useStandaloneDimensions) {
      const vpW = window.innerWidth || 0;
      const vpH = window.innerHeight || 0;

      const collapsedWidthPx = resolvePx(collapsedWidth, vpW);
      const expandedWidthPx =
        expandedWidth === undefined ? Infinity : resolvePx(expandedWidth, vpW);

      if (collapsedWidthPx > expandedWidthPx) {
        logWarn(
          'Widget',
          `sizeConstraints: resolved minWidth (${collapsedWidthPx}px) exceeds resolved maxWidth (${expandedWidthPx}px). ` +
            'Falling back to default widths (425 collapsed / 425 expanded).'
        );
        collapsedWidth = 425;
        expandedWidth = hostResizable ? undefined : 425;
      }

      const collapsedHeightPx = resolvePx(collapsedHeight, vpH);
      const expandedHeightPx =
        expandedHeight === undefined
          ? Infinity
          : resolvePx(expandedHeight, vpH);

      if (collapsedHeightPx > expandedHeightPx) {
        const defaultExpH = widgetMaxHeightSize();
        logWarn(
          'Widget',
          `sizeConstraints: resolved minHeight (${collapsedHeightPx}px) exceeds resolved maxHeight (${expandedHeightPx}px). ` +
            `Falling back to default heights (200 collapsed / ${defaultExpH} expanded).`
        );
        collapsedHeight = 200;
        expandedHeight = hostResizable ? undefined : defaultExpH;
      }
    }

    const positionWidth = useStandaloneDimensions
      ? 400
      : typeof collapsedWidth === 'number'
        ? collapsedWidth
        : 425;
    const positionHeight = useStandaloneDimensions
      ? 600
      : typeof collapsedHeight === 'number'
        ? collapsedHeight
        : 200;

    const margin = 20;

    const viewportWidth = window.innerWidth || 0;
    const topMargin = 60;

    const defaultInitialPosition = {
      x: Math.max(margin, viewportWidth - positionWidth - margin),
      y: topMargin,
    };

    super({
      ...rest,
      title: brand?.brandName || 'Rheo AI Scribe',
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- React version compatibility between widget-core and this package
      appComponent: AppComponent as any,
      initialSize: useStandaloneDimensions
        ? { width: positionWidth, height: positionHeight }
        : undefined,
      initialPosition: defaultInitialPosition,
      sizeConstraints: {
        minWidth: collapsedWidth,
        maxWidth: useStandaloneDimensions ? undefined : expandedWidth,
        minHeight: useStandaloneDimensions ? 600 : collapsedHeight,
        maxHeight: useStandaloneDimensions ? undefined : expandedHeight,
      },
      startCollapsed: !useStandaloneDimensions,
      capabilities: {
        draggable: true,
        resizable: false,
        canToggleFullscreen: false,
        allowExpandCollapse: !useStandaloneDimensions,
        allowClose: true,
        ...rest.capabilities,
      },
      initialContext: mergedContext,
      // Pass brand configuration for theming
      brand,
    });

    this.hostSeamlessAuth = hostSeamlessAuth;
  }

  /**
   * Caps the widget to one live instance per tab. If another instance is
   * genuinely still mounted, refuse this mount (leaving the first untouched)
   * rather than let a second instance clobber the shared WhisperClient. The
   * guard self-heals: a tracked instance that is no longer mounted is treated
   * as stale, so a crashed/orphaned ref can never permanently lock out mounting.
   * `isMounted()` is the only public liveness signal reachable from a subclass
   * (domManager is private), so it is the authority here.
   */
  public mount(): this {
    const active = AmbientScribeWidget.activeInstance;
    const otherIsLive =
      active !== null && active !== this && active.isMounted();

    if (otherIsLive) {
      logWarn(
        'Widget',
        'Refusing to mount a second AI Scribe widget: one instance is already active in this tab. Concurrent scribe widgets are not supported and would corrupt the active recording.'
      );
      this.emitToHostListeners('error', {
        message:
          'An AI Scribe widget is already open in this tab. Close it before opening another.',
      });
      return this;
    }

    AmbientScribeWidget.activeInstance = this;

    if (this.hostSeamlessAuth) {
      setHostSeamlessAuthConfig(this.hostSeamlessAuth);
      logDebug('Widget', 'Host seamless auth configured');
    } else {
      setHostSeamlessAuthConfig(null);
    }

    return super.mount();
  }

  /**
   * Runs normal teardown, then releases this instance's claim on the per-tab
   * slot only once it has actually unmounted. `super.unmount()` can veto
   * teardown (e.g. a save/discard prompt while recording keeps the widget
   * mounted), so the slot must not be freed until `isMounted()` confirms this
   * instance is gone — otherwise a second widget could mount and clobber the
   * still-live one's shared WhisperClient.
   */
  public unmount(): this {
    const result = super.unmount();
    if (AmbientScribeWidget.activeInstance === this && !this.isMounted()) {
      AmbientScribeWidget.activeInstance = null;
    }
    return result;
  }
}
